import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";

import API from "../api";

const STATUS_STYLES = {
  active: "border-green-400/50 text-green-300",
  pending: "border-amber-400/50 text-amber-300",
  redeemed: "border-ivory/30 text-ivory/60",
  refunded: "border-red-400/50 text-red-300",
};

const euro = (n) => `€${Number(n).toLocaleString("en-IE", { maximumFractionDigits: 2 })}`;

export default function VouchersManager() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/vouchers`, { credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setVouchers(data.vouchers || []);
    } catch {
      toast.error("Could not load vouchers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (v) => {
    setBusyId(v.id);
    try {
      const res = await fetch(`${API}/api/admin/vouchers/${v.id}/redeem`, {
        method: "PATCH",
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      toast.success(v.status === "active" ? `${v.code} marked as redeemed.` : `${v.code} is active again.`);
      await load();
    } catch {
      toast.error("Could not update the voucher.");
    } finally {
      setBusyId("");
    }
  };

  const resend = async (v) => {
    setBusyId(v.id);
    try {
      const res = await fetch(`${API}/api/admin/vouchers/${v.id}/resend`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "failed");
      toast.success(`Voucher ${v.code} emailed again.`);
    } catch (err) {
      toast.error(err.message || "Could not resend the email.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="pb-20">
      <div className="mt-6 flex items-center justify-between gap-4">
        <p className="text-xs uppercase tracking-widest2 text-coastal" data-testid="vouchers-count">
          {vouchers.length} {vouchers.length === 1 ? "voucher" : "vouchers"} · newest first
        </p>
        <button
          onClick={load}
          data-testid="vouchers-refresh"
          className="flex items-center gap-2 border border-ivory/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest2 text-ivory/80 transition-colors hover:border-gold hover:text-gold-2"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <div className="mt-6 flex flex-col gap-4" data-testid="vouchers-list">
        {!loading && vouchers.length === 0 && (
          <p className="mt-16 text-center font-display text-2xl italic text-ivory/50" data-testid="vouchers-empty">
            No vouchers yet — the first sale will appear here the moment it's paid.
          </p>
        )}
        {vouchers.map((v) => (
          <div key={v.id} className="border border-ivory/15 bg-navy-2 p-5" data-testid={`voucher-row-${v.code}`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-mono text-lg tracking-widest text-gold-2" data-testid={`voucher-code-${v.code}`}>
                  {v.code}
                </p>
                <p className="mt-1 text-sm font-light text-ivory/80">
                  {euro(v.amount)} · bought by {v.buyer_name} ({v.buyer_email})
                </p>
                {v.recipient_email ? (
                  <p className="text-xs font-light text-ivory/60">
                    Gift for {v.recipient_name || "—"} ({v.recipient_email})
                  </p>
                ) : (
                  <p className="text-xs font-light text-ivory/60">Sent to the buyer</p>
                )}
                {v.expiry_date && <p className="text-xs font-light text-ivory/50">Valid until {v.expiry_date}</p>}
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`border px-3 py-1 text-[10px] font-semibold uppercase tracking-widest2 ${STATUS_STYLES[v.status] || "border-ivory/30 text-ivory/60"}`}
                  data-testid={`voucher-status-${v.code}`}
                >
                  {v.status}
                </span>
                {v.status !== "pending" && v.status !== "refunded" && (
                  <button
                    onClick={() => toggle(v)}
                    disabled={busyId === v.id}
                    data-testid={`voucher-redeem-${v.code}`}
                    className="border border-ivory/25 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest2 text-ivory/80 transition-colors hover:border-gold hover:text-gold-2 disabled:opacity-60"
                  >
                    {busyId === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : v.status === "active" ? "Mark redeemed" : "Mark active"}
                  </button>
                )}
                {v.status === "active" && (
                  <button
                    onClick={() => resend(v)}
                    disabled={busyId === v.id}
                    data-testid={`voucher-resend-${v.code}`}
                    className="border border-gold/60 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest2 text-gold-2 transition-colors hover:bg-gold hover:text-navy disabled:opacity-60"
                  >
                    Resend email
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
